import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { BookOpen, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Select } from '../../ui/newSelect';
import { Alert, AlertDescription } from '../../ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import Breadcrumb from '../../Common/Breadcrumb';
import ConsultantPricingSchedule from '../../Agreements/ConsultantPricingSchedule';
import { Actions as AGREEMENTS } from '../../../store/actions/agreementsActions';
import { BILLING_CURRENCY_OPTIONS } from '../../utils/stripeCurrency';

/**
 * Sales Portal read view of the live Catalog: Plan × FTE License Prices and
 * Add-ons with Foundation / Growth / Scale. Same payload snapshotted into new
 * Consultant Master Agreements.
 */
const CatalogPricingPage = ({ getConsultantPricingSchedule }) => {
  const [currency, setCurrency] = useState('£');
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getConsultantPricingSchedule(currency).then((result) => {
      if (cancelled) return;
      setSchedule(result || null);
      setError(result ? null : 'No complete catalog is available for this currency.');
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currency, getConsultantPricingSchedule]);

  return (
    <div className='mx-auto max-w-6xl space-y-4 p-4'>
      <Breadcrumb
        items={[
          { label: 'Sales Portal', link: '/admin/agreements' },
          { label: 'Catalog pricing' },
        ]}
      />

      <Card className='bg-card text-card-foreground'>
        <CardHeader className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <CardTitle className='flex items-center gap-2 text-lg'>
              <BookOpen size={20} strokeWidth={1.5} />
              Catalog pricing
            </CardTitle>
            <p className='mt-1 text-sm text-muted-foreground'>
              Live Plan × FTE matrix and Add-ons used for new Consultant Master Agreements. Accepted
              agreements keep the schedule they signed; they do not update here.
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Select
              className='w-36'
              value={currency}
              onChange={setCurrency}
              options={BILLING_CURRENCY_OPTIONS}
              searchable={false}
              clearable={false}
            />
            <Button variant='outline' size='sm' asChild>
              <Link to='/admin/addOnProducts'>Manage add-on products</Link>
            </Button>
            <Button size='sm' asChild>
              <Link to='/admin/agreements/new'>New agreement</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className='flex items-center gap-2 py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
              Loading catalog for {currency}…
            </div>
          )}
          {!loading && error && (
            <Alert variant='destructive'>
              <AlertDescription>
                {error} Only currencies with a full Plan × FTE matrix can be offered. GBP is the
                default Catalog currency today.
              </AlertDescription>
            </Alert>
          )}
          {!loading && schedule && <ConsultantPricingSchedule schedule={schedule} />}
        </CardContent>
      </Card>
    </div>
  );
};

const mapDispatch = (dispatch) => ({
  getConsultantPricingSchedule: (currency) => dispatch(AGREEMENTS.getConsultantPricingSchedule(currency)),
});

export default connect(null, mapDispatch)(CatalogPricingPage);
